import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, UserCheck, UserX, Wallet, ArrowUpRight,
  Calendar, Activity, Briefcase, Hand, PartyPopper,
  AlertCircle, ChevronLeft, ChevronRight, Bell,
  Sparkles, TrendingUp, Check, Eye, Zap, Package,
  Clock, Pause, Play, RefreshCw, Loader2 
} from 'lucide-react';
import api from '../services/api';

// ============================================================================
// MAP icône — icon_name (backend) → composant Lucide
// ============================================================================

const ICON_MAP = {
  Zap, Package, Clock, Sparkles, TrendingUp,
  Bell, AlertCircle, CheckCircle2: Check,
};
const getIcon = (name) => ICON_MAP[name] || Zap;

// ============================================================================
// MAP gradient — classe Tailwind (backend) → style inline
// ============================================================================

const GRADIENT_MAP = {
  'from-emerald-500 to-teal-500':  'linear-gradient(135deg, #10b981, #14b8a6)',
  'from-[#448696] to-[#6fa3b0]':   'linear-gradient(135deg, #448696, #6fa3b0)',
  'from-amber-500 to-orange-500':  'linear-gradient(135deg, #f59e0b, #f97316)',
  'from-violet-500 to-purple-600': 'linear-gradient(135deg, #8b5cf6, #9333ea)',
  'from-[#fed774] to-[#fbb840]':   'linear-gradient(135deg, #fed774, #fbb840)',
  'from-red-500 to-rose-600':      'linear-gradient(135deg, #ef4444, #e11d48)',
  'from-sky-500 to-blue-600':      'linear-gradient(135deg, #0ea5e9, #2563eb)',
  'from-slate-600 to-slate-800':   'linear-gradient(135deg, #475569, #1e293b)',
};
const getGradient = (g) => GRADIENT_MAP[g] || 'linear-gradient(135deg, #448696, #6fa3b0)';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

// ============================================================================
// SOUS-COMPOSANT : Bloc Communications (API réelle)
// ============================================================================

const TYPE_CONFIG = {
  nouveaute: { label: 'NOUVEAUTÉ', color: '#448696', bg: 'rgba(68,134,150,0.07)'  },
  upsell:    { label: 'UPSELL',    color: '#8b5cf6', bg: 'rgba(139,92,246,0.07)' },
  alerte:    { label: 'ALERTE',    color: '#ef4444', bg: 'rgba(239,68,68,0.07)'  },
  info:      { label: 'INFO',      color: '#10b981', bg: 'rgba(16,185,129,0.07)' },
};

const CommunicationsBlock = () => {
  const [communications, setCommunications] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [idx,      setIdx]      = useState(0);
  const [dir,      setDir]      = useState('');
  const [inAnim,   setInAnim]   = useState(false);
  const [paused,   setPaused]   = useState(false);
  const [ringing,  setRinging]  = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.communications.getAll();
        if (res.success && Array.isArray(res.data)) setCommunications(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const unread = communications.filter(c => !c.is_read).length;

  useEffect(() => {
    if (!unread) return;
    const ring = () => { setRinging(true); setTimeout(() => setRinging(false), 700); };
    ring();
    const iv = setInterval(ring, 5500);
    return () => clearInterval(iv);
  }, [unread]);

  const goTo = (newIdx, direction) => {
    if (inAnim || communications.length < 2 || newIdx === idx) return;
    setDir(direction);
    setInAnim(true);
    setTimeout(() => { setIdx(newIdx); setInAnim(false); }, 280);
  };
  const next = () => goTo((idx + 1) % communications.length, 'next');
  const prev = () => goTo((idx - 1 + communications.length) % communications.length, 'prev');



useEffect(() => {
    clearInterval(timerRef.current);
    if (!paused && communications.length > 1) {
      timerRef.current = setInterval(() => {
        setIdx(prev => (prev + 1) % communications.length);
        setDir('next');
        setInAnim(true);
        setTimeout(() => setInAnim(false), 280);
      }, 6500);
    }
    return () => clearInterval(timerRef.current);
  }, [paused, communications.length]);



  const markRead = async (id) => {
    setCommunications(p => p.map(c => c.id === id ? { ...c, is_read: true } : c));
    try { await api.communications.markAsRead(id); } catch (e) {}
   
  };

if (loading) return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden flex items-center justify-center"
      style={{ minHeight: 420, boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.04)' }}>
      <Loader2 size={22} className="animate-spin" style={{ color: '#448696' }} />
    </div>
  );

  if (!communications.length) return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden flex items-center justify-center"
      style={{ minHeight: 420, boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.04)' }}>
      <div className="text-center px-8">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-3"
          style={{ background: '#f0f9fb', border: '1px solid #ddf0f5' }}>
          <Bell size={20} style={{ color: '#448696' }} />
        </div>
        <p className="text-sm font-semibold text-slate-500 mb-1">Aucune communication</p>
        <p className="text-xs text-slate-400">Les messages HoraPilot apparaîtront ici</p>
      </div>
    </div>
  );

  const comm = communications[idx];
  const Icon = getIcon(comm.icon_name);
  const tc   = TYPE_CONFIG[comm.type] || TYPE_CONFIG.info;

  return (
    <div className="bg-white rounded-3xl overflow-hidden flex flex-col"
      style={{ height: 420, border: '1px solid #eaeff5', boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.04)' }}>

      {/* EN-TÊTE */}
      <div className="flex items-center justify-between border-b border-slate-100"
        style={{ padding: '18px 22px 16px', background: '#fafcfe' }}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: unread > 0 ? '#fff5f5' : '#f8fafc',
              border: `1px solid ${unread > 0 ? '#fecaca' : '#e2e8f0'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Bell size={16} style={{
                color: unread > 0 ? '#ef4444' : '#94a3b8',
                animation: ringing && unread > 0 ? 'bellShake 0.6s ease-in-out' : 'none',
              }} />
            </div>
            {unread > 0 && (
              <div style={{
                position: 'absolute', top: -4, right: -4,
                width: 16, height: 16, borderRadius: '50%',
                background: '#ef4444', border: '2px solid white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, fontWeight: 800, color: 'white',
              }}>{unread}</div>
            )}
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 leading-tight">Informations & communications</p>
            <p className="text-xs font-medium mt-0.5" style={{ color: '#94a3b8' }}>
              {unread > 0 ? `${unread} non lu${unread > 1 ? 's' : ''}` : 'Tout est à jour ✓'}
            </p>
          </div>
        </div>

        {/* Pause — très discret */}
        <button onClick={() => setPaused(p => !p)} title={paused ? 'Reprendre' : 'Pause'}
          style={{
            width: 28, height: 28, borderRadius: 8, background: 'transparent',
            border: '1px solid #e8edf2', display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', padding: 0,
            color: paused ? '#f59e0b' : '#cbd5e1',
          }}>
          {paused ? <Play size={11} /> : <Pause size={11} />}
        </button>
      </div>

      {/* CORPS */}
      <div className="flex-1 overflow-hidden" style={{ padding: '20px 22px' }}>
        <div
          key={idx}
          className={dir === 'next' ? 'slide-next' : dir === 'prev' ? 'slide-prev' : ''}
          style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%', opacity: inAnim ? 0 : 1, transition: 'opacity 0.15s' }}
        >
          {/* Type + date */}
          <div className="flex items-center justify-between">
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
              padding: '3px 9px', borderRadius: 4,
              background: tc.bg, color: tc.color,
            }}>{tc.label}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: '#94a3b8' }}>{formatDate(comm.created_at)}</span>
              {!comm.is_read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />}
            </div>
          </div>

          {/* Icône + titre */}
          <div className="flex items-start gap-3">
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: getGradient(comm.gradient),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 6px 18px ${comm.badge_color || '#448696'}35`,
            }}>
              <Icon size={21} style={{ color: 'white' }} />
            </div>
            <div style={{ flex: 1, paddingTop: 2 }}>
              {comm.badge && (
                <p style={{ fontSize: 10, fontWeight: 700, color: comm.badge_color || '#448696', margin: '0 0 4px', letterSpacing: '0.03em' }}>
                  {comm.badge}
                </p>
              )}
              <h3 className="font-bold text-slate-900" style={{ fontSize: 15, lineHeight: 1.38, margin: 0 }}>
                {comm.titre}
              </h3>
            </div>
          </div>

          {/* Séparateur */}
          <div style={{ height: 1, background: '#f1f5f9' }} />


          {/* Contenu */}
        <div style={{ overflow: 'hidden', flex: 1 }}>
            <p className="text-sm text-slate-500" style={{ lineHeight: 1.72, margin: 0,
              display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {comm.contenu}
            </p>
          </div>


          {/* Marquer lu */}
          <div>
            {!comm.is_read ? (
              <button onClick={() => markRead(comm.id)}
                className="flex items-center gap-2 text-xs font-semibold transition-all"
                style={{ padding: '7px 14px', borderRadius: 8, background: '#f0f9fb', border: '1px solid #ddf0f5', color: '#448696', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#e0f2f7'}
                onMouseLeave={e => e.currentTarget.style.background = '#f0f9fb'}>
                <Check size={12} /> Marquer comme lu
              </button>
            ) : (
              <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#94a3b8' }}>
                <Eye size={12} /> Lu
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PIED DE PAGE */}
      <div className="flex items-center justify-between border-t border-slate-100"
        style={{ padding: '11px 22px 14px', background: '#fafcfe' }}>
        <span className="text-xs font-medium" style={{ color: '#cbd5e1', minWidth: 36 }}>
          {idx + 1} / {communications.length}
          {paused && <span style={{ marginLeft: 5, color: '#fbbf24', fontSize: 10 }}>⏸</span>}
        </span>

        {/* Dots discrets */}
        <div className="flex items-center gap-1.5">
          {communications.map((m, i) => (
            <button key={m.id}
              onClick={() => goTo(i, i > idx ? 'next' : 'prev')}
              style={{
                border: 'none', cursor: 'pointer', padding: 0, borderRadius: 99,
                transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
                width: i === idx ? 16 : 5, height: 5,
                background: i === idx ? '#448696' : m.is_read ? '#e8edf2' : '#b2d8e0',
              }} />
          ))}
        </div>

        {/* Flèches */}
      <div className="flex gap-1.5">
          {[
            { action: () => prev(), icon: <ChevronLeft size={13} />, filled: false },
            { action: () => next(), icon: <ChevronRight size={13} />, filled: true  },
          ].map(({ action, icon, filled }, i) => (
            <button key={i} onClick={action}
              className="flex items-center justify-center transition-all"
              style={{
                width: 28, height: 28, borderRadius: 8, padding: 0, cursor: 'pointer',
                background: filled ? '#448696' : 'white',
                border: filled ? '1px solid #448696' : '1px solid #e2e8f0',
                color: filled ? 'white' : '#94a3b8',
              }}
              onMouseEnter={e => { if (!filled) { e.currentTarget.style.borderColor = '#448696'; e.currentTarget.style.color = '#448696'; } else e.currentTarget.style.opacity = '0.82'; }}
              onMouseLeave={e => { if (!filled) { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#94a3b8'; } else e.currentTarget.style.opacity = '1'; }}>
              {icon}
            </button>
          ))}
        </div>
      </div>
   
    </div>
  );
};

// ============================================================================
// SOUS-COMPOSANT : Carousel Indicateurs + Résumé du jour
// ============================================================================

const IndicateursCarousel = ({ stats, performances }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating]   = useState(false);

  const goTo = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setTimeout(() => { setCurrentSlide(index); setIsAnimating(false); }, 250);
  };

  useEffect(() => {
    const iv = setInterval(() => setCurrentSlide(p => (p + 1) % 2), 8000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-slate-200 overflow-hidden flex flex-col" style={{ height: '420px' }}>
      <div className="flex border-b border-slate-100">
        {[{ label: 'Indicateurs Clés', icon: Activity }, { label: 'Résumé du Jour', icon: ArrowUpRight }].map(({ label, icon: Icon }, i) => (
          <button key={i} onClick={() => goTo(i)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-semibold transition-all"
            style={{ color: currentSlide === i ? '#448696' : '#94a3b8', borderBottom: currentSlide === i ? '2px solid #448696' : '2px solid transparent', backgroundColor: currentSlide === i ? '#f0f9fb' : 'transparent' }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 p-6 transition-all duration-250" style={{ opacity: isAnimating ? 0 : 1 }}>
        {currentSlide === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            {performances.map((perf, i) => {
              const Icon = perf.icon;
              const pct  = perf.label === 'Taux de présence' ? Math.min(perf.value, 100) : 100;
              return (
                <div key={i} className="relative p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100">
                  <Icon className="w-7 h-7 text-slate-400 mb-3" />
                  <div className="mb-3">
                    <div className="text-3xl font-bold text-slate-900 mb-1">{perf.value}{perf.label === 'Taux de présence' ? '%' : ''}</div>
                    <div className="text-sm font-medium text-slate-600">{perf.label}</div>
                  </div>
                  {perf.label === 'Taux de présence' && (
                    <div className="relative w-20 h-20 mx-auto mt-2">
                      <svg className="transform -rotate-90 w-20 h-20">
                        <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-slate-200" />
                        <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="transparent"
                          strokeDasharray={`${2 * Math.PI * 32}`}
                          strokeDashoffset={`${2 * Math.PI * 32 * (1 - pct / 100)}`}
                          className={perf.value >= perf.target ? 'text-[#448696]' : 'text-[#fed774]'}
                          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-slate-600 text-center leading-tight">Obj.<br/>{perf.target}%</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4 h-full">
            {[
              { label: 'Taux de présence', value: stats.tauxPresence, color: '#22c55e', textColor: 'text-green-600',
                sub: `${stats.presentsAujourdhui} présents sur ${stats.totalEmployes} employés` },
              { label: "Taux d'absence", value: 100 - stats.tauxPresence, color: '#ef4444', textColor: 'text-red-600',
                sub: `${stats.absentsPointes} absents pointés + ${stats.aucunPointage} sans pointage = ${stats.absentsAujourdhui} total` }
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-600">{item.label}</span>
                  <span className={`text-2xl font-bold ${item.textColor}`}>{item.value}%</span>
                </div>
                <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${item.value}%`, backgroundColor: item.color }} />
                </div>
                <p className="text-xs text-slate-500 mt-2">{item.sub}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pb-4 flex justify-center gap-2">
        {[0, 1].map(i => (
          <button key={i} onClick={() => goTo(i)}
            className="rounded-full transition-all duration-300"
            style={{ width: i === currentSlide ? '24px' : '8px', height: '8px', backgroundColor: i === currentSlide ? '#448696' : '#cbd5e1' }} />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL : Dashboard
// ============================================================================

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEmployes: 0, presentsAujourdhui: 0, absentsPointes: 0,
    aucunPointage: 0, absentsAujourdhui: 0, masseSalariale: 0,
    tauxPresence: 0, nombreDepartements: 0
  });
  const [loading, setLoading]     = useState(true);
  const [salutation, setSalutation] = useState('');

  useEffect(() => {
    const h = new Date().getHours();
    setSalutation(h >= 5 && h < 12 ? 'Bonjour' : h >= 12 && h < 18 ? 'Bon après-midi' : 'Bonsoir');
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const now   = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      const [rapportRes, globalesRes] = await Promise.all([
        api.rapports.getRapportJournalier(today),
        api.rapports.getStatistiquesGlobales(now.getMonth()+1, now.getFullYear())
      ]);
      if (rapportRes.success && globalesRes.success) {
        setStats({
          totalEmployes:      rapportRes.data.employes_actifs,
          presentsAujourdhui: rapportRes.data.presents,
          absentsPointes:     rapportRes.data.absents_pointes || 0,
          aucunPointage:      rapportRes.data.aucun_pointage  || 0,
          absentsAujourdhui:  rapportRes.data.total_absents   || rapportRes.data.absents,
          tauxPresence:       rapportRes.data.taux_presence,
          masseSalariale:     globalesRes.data.masse_salariale,
          nombreDepartements: globalesRes.data.nombre_departements
        });
      }
    } catch (err) {
      console.error('❌ [Dashboard]', err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (m) => m >= 1000000 ? `${(m/1000000).toFixed(1)}M` : m >= 1000 ? `${(m/1000).toFixed(0)}K` : m.toLocaleString();

  const mainStats = [
    { label: 'Employés',        value: stats.totalEmployes,        icon: Users,       gradient: 'from-[#448696] to-[#a8d5e2]', sparkline: [20,35,28,42,38,45,50] },
    { label: 'Présents',        value: stats.presentsAujourdhui,   icon: UserCheck,   gradient: 'from-green-500 to-green-400',  sparkline: [35,38,42,40,44,46,48] },
    { label: 'Absents Pointés', value: stats.absentsPointes || 0,  icon: UserX,       gradient: 'from-red-500 to-red-400',      sparkline: [5,4,3,2,3,2,1]       },
    { label: 'Aucun Pointage',  value: stats.aucunPointage  || 0,  icon: AlertCircle, gradient: 'from-amber-500 to-amber-400',  sparkline: [10,8,6,5,4,3,2]      },
    { label: 'Masse Salariale', value: fmt(stats.masseSalariale),   icon: Wallet,      gradient: 'from-[#fed774] to-[#ffe49a]', sparkline: [2.1,2.3,2.2,2.5,2.4,2.6,2.8], subValue: 'FCFA' }
  ];

  const performances = [
    { label: 'Taux de présence', value: stats.tauxPresence,       target: 95,                      icon: Activity  },
    { label: 'Départements',     value: stats.nombreDepartements, target: stats.nombreDepartements, icon: Briefcase }
  ];

  if (loading) return null;

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#fdfcf5' }}>

      {/* HEADER */}
      <div className="mb-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl font-bold mb-3 flex items-center gap-3" style={{ color: '#448696' }}>
              {salutation} <Hand className="w-12 h-12" style={{ color: '#448696' }} />
            </h1>
            <p className="text-lg text-slate-600 font-medium">Voici un aperçu de votre entreprise aujourd'hui</p>
          </div>
          <div className="px-6 py-3 bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center gap-3">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {mainStats.map((stat, i) => {
          const Icon = stat.icon;
          const max  = Math.max(...stat.sparkline);
          return (
            <div key={i}
              className="group relative bg-white rounded-3xl p-4 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-200 overflow-hidden"
              style={{ animationDelay: `${i*100}ms`, animation: 'slideUp 0.6s ease-out forwards' }}>
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
              <div className="relative z-10">
                <div className="mb-4">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mb-3">
                  <div className="text-3xl font-bold text-slate-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                    {stat.value}
                    {stat.subValue && <span className="text-sm text-slate-500 ml-2 font-medium">{stat.subValue}</span>}
                  </div>
                  <div className="text-xs font-medium text-slate-500">{stat.label}</div>
                </div>
                <div className="flex items-end gap-1 h-6">
                  {stat.sparkline.map((v, j) => (
                    <div key={j}
                      className={`flex-1 rounded-t-sm bg-gradient-to-t ${stat.gradient} transition-all duration-300 group-hover:opacity-100 opacity-60`}
                      style={{ height: `${(v/max)*100}%` }} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* BLOC PRINCIPAL */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <IndicateursCarousel stats={stats} performances={performances} />
        <CommunicationsBlock />
      </div>


      <style jsx>{`
        @keyframes slideUp  { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        @keyframes scaleIn  { from { opacity:0; transform:scale(0.9); }       to { opacity:1; transform:scale(1); } }
        @keyframes bellRing {
          0%  {transform:rotate(0deg);}  15% {transform:rotate(18deg);}  30% {transform:rotate(-16deg);}
          45% {transform:rotate(12deg);} 60% {transform:rotate(-10deg);} 75% {transform:rotate(6deg);}
          90% {transform:rotate(-4deg);} 100%{transform:rotate(0deg);}
        }
        @keyframes progressBar { from { width:0%; } to { width:100%; } }

        @keyframes fadeSlideNext { from{opacity:0;transform:translateX(14px)} to{opacity:1;transform:translateX(0)} }
        @keyframes fadeSlidePrev { from{opacity:0;transform:translateX(-14px)} to{opacity:1;transform:translateX(0)} }
        @keyframes bellShake {
          0%,100%{transform:rotate(0)}
          18%{transform:rotate(13deg)} 36%{transform:rotate(-11deg)}
          54%{transform:rotate(7deg)}  72%{transform:rotate(-4deg)}
        }
        .slide-next { animation: fadeSlideNext 0.3s cubic-bezier(0.16,1,0.3,1); }
        .slide-prev { animation: fadeSlidePrev 0.3s cubic-bezier(0.16,1,0.3,1); }

        @keyframes scrollText {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-100%); }
        }

      `}</style>
    </div>
  );
};

export default Dashboard;